/**
 * Feb-2026 — Supplier Email Composer (3-step wizard).
 *
 * Simplified from the earlier 4-step RFQ wizard. Now a free-form
 * supplier email composer: Write Email → Select Suppliers →
 * Review & Send. All structured RFQ fields (products, destination,
 * budget, warranty, etc.) live under a collapsed Optional Details
 * section on Step 1 and are never required.
 *
 * Reuses the existing backend endpoints unchanged: previewRfq,
 * rfqRecipientPreview, rfqTestSend, rfqSchedule, sendRfqCampaign,
 * createRfqCampaign, updateRfqCampaign, listSuppliers.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  X, ChevronLeft, ChevronRight, ChevronDown, Plus, Copy, Trash2,
  ArrowUp, ArrowDown, Send, Eye, Save, Users, Calendar, TestTube2,
  CheckCircle2, AlertTriangle,
} from 'lucide-react';
import {
  createRfqCampaign, updateRfqCampaign, sendRfqCampaign, listSuppliers,
  previewRfq, listRfqPrebuiltTemplates, rfqRecipientPreview, rfqTestSend,
  rfqSchedule,
} from '../../lib/rfqApi';
import {
  SUPPLIER_TYPE_SEGMENTS, matchesTypeSegment, getSegmentByValue,
  getSegmentLabel, countByType, flagRestrictedSuppliers,
  RESTRICTED_TYPES, DO_NOT_RFQ_TYPE,
} from '../../lib/supplierTypeSegments';

const SHIPPING_TERMS = ['EXW', 'FOB', 'CIF', 'DDP', 'Flexible'];
const CONDITIONS = ['New', 'Grade A', 'Grade B', 'Grade C', 'Mixed'];
const WARRANTIES = ['None', '30 days', '90 days', '1 year', 'Flexible'];
const RECENT_CONTACT_OPTIONS = [
  { value: 0, label: 'None (include all)' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];
const EMPTY_PRODUCT = {
  brand: '', model: '', cpu_generation: '', ram: '', storage: '',
  screen_size: '', grade: '', quantity: '', notes: '',
};
const EMPTY_FORM = {
  campaign_name: '',
  destination_country: '',
  delivery_location: '',
  target_total_quantity: '',
  target_budget: '',
  response_due_date: '',
  shipping_term: '',
  condition: '',
  warranty: '',
  additional_requirements: '',
  products: [],
  product_request_text: '',
  supplier_ids: [],
  email_subject: '',
  email_body: '',
  preheader: '',
  reply_to: '',
  sender_profile_id: '',
  template_key: '',
  scheduled_at: '',
  exclude_recent_contact_days: 0,
  type_segment: '',
};

const STEP_LABELS = ['Write Email', 'Select Suppliers', 'Review & Send'];

// ─── Auto-suggest campaign name from subject + date ────────────────
const suggestCampaignName = (form) => {
  const subj = (form.email_subject || '').trim();
  const today = new Date().toISOString().slice(0, 10);
  return subj ? `${subj} — ${today}` : `Supplier Email — ${today}`;
};

// ─── Structured products → free-text block (Optional Details) ──────
const productsToText = (products) => {
  if (!products || !products.length) return '';
  return products
    .map((p) => {
      const parts = [];
      if (p.brand) parts.push(p.brand);
      if (p.model) parts.push(p.model);
      if (p.cpu_generation) parts.push(p.cpu_generation);
      if (p.ram) parts.push(`${p.ram} RAM`);
      if (p.storage) parts.push(`${p.storage} storage`);
      if (p.screen_size) parts.push(`${p.screen_size} screen`);
      if (p.grade) parts.push(`${p.grade} grade`);
      const line = parts.join(', ');
      const qty = p.quantity ? ` — ${p.quantity} units` : '';
      const notes = p.notes ? ` (${p.notes})` : '';
      const full = `${line}${qty}${notes}`.trim();
      return full ? `- ${full}` : '';
    })
    .filter(Boolean)
    .join('\n');
};

export const RfqWizard = ({ open, editing, onClose, onSaved }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [campaignId, setCampaignId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [manualNameEdit, setManualNameEdit] = useState(false);

  // Suppliers step state
  const [suppliers, setSuppliers] = useState([]);
  const [supFilters, setSupFilters] = useState({
    q: '', country: '', tag: '', status: '',
    email_verification_status: '', enrichment_status: '',
  });
  // Confirmation prompt when a filter change would drop existing
  // selections. `pendingFilter` holds the field+value the operator
  // just picked; a modal below asks whether to drop non-matching
  // selections before we actually apply it.
  const [pendingFilter, setPendingFilter] = useState(null);

  // Review & Send state
  const [previewData, setPreviewData] = useState(null);
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);

  const readOnly = editing?.status && !['draft', 'scheduled'].includes(editing.status);

  // ── Load templates + editing state on open ────────────────────
  useEffect(() => {
    if (!open) return;
    listRfqPrebuiltTemplates().then((r) => setTemplates(r.data.items || [])).catch(() => {});
    if (editing) {
      setForm({
        ...EMPTY_FORM,
        ...editing,
        products: editing.products || [],
        target_total_quantity: editing.target_total_quantity ?? '',
        target_budget: editing.target_budget ?? '',
        exclude_recent_contact_days: editing.exclude_recent_contact_days ?? 0,
      });
      setCampaignId(editing.id);
      setManualNameEdit(true);  // don't overwrite an existing name
    } else {
      // Feb-2026 simplification — new campaigns start BLANK. Subject
      // and body are only populated if the user picks a template.
      setForm({ ...EMPTY_FORM });
      setCampaignId(null);
      setManualNameEdit(false);
    }
    setStep(0);
    setRecipientPreview(null);
    setPreviewData(null);
    setConfirmSend(false);
  }, [open, editing]);

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Auto-suggest campaign name from subject+date unless the user has
  // manually edited the name (or is editing an existing campaign).
  useEffect(() => {
    if (manualNameEdit) return;
    setForm((f) => {
      if (!f.email_subject.trim()) return f;
      return { ...f, campaign_name: suggestCampaignName(f) };
    });
  }, [form.email_subject, manualNameEdit]);

  // ── Products (Optional Details) ────────────────────────────────
  const updateProduct = (idx, patch) => {
    setForm((f) => {
      const next = f.products.map((p, i) => (i === idx ? { ...p, ...patch } : p));
      return { ...f, products: next };
    });
  };
  const addProduct = () => setForm((f) => ({ ...f, products: [...f.products, { ...EMPTY_PRODUCT }] }));
  const duplicateProduct = (idx) => setForm((f) => {
    const copy = { ...f.products[idx] };
    return { ...f, products: [...f.products.slice(0, idx + 1), copy, ...f.products.slice(idx + 1)] };
  });
  const deleteProduct = (idx) => setForm((f) => ({ ...f, products: f.products.filter((_, i) => i !== idx) }));
  const moveProduct = (idx, dir) => setForm((f) => {
    const j = idx + dir;
    if (j < 0 || j >= f.products.length) return f;
    const next = [...f.products];
    [next[idx], next[j]] = [next[j], next[idx]];
    return { ...f, products: next };
  });

  // ── Insert Structured Product Request into the body ────────────
  const insertStructuredProductRequest = () => {
    const block = productsToText(form.products);
    if (!block) { toast.error('Add at least one product row first'); return; }
    setForm((f) => ({
      ...f,
      email_body: (f.email_body || '') + (f.email_body?.endsWith('\n') ? '' : '\n\n') + block + '\n',
    }));
    toast.success('Product request inserted into the email body');
  };

  // ── Fetch suppliers (Step 2) ───────────────────────────────────
  const fetchSuppliers = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(supFilters).filter(([, v]) => v));
      const { data } = await listSuppliers(params);
      setSuppliers(data.items || []);
    } catch { /* ignore */ }
  }, [supFilters]);
  useEffect(() => { if (open && step === 1) fetchSuppliers(); }, [open, step, fetchSuppliers]);

  const toggleSupplier = (id) => updateForm({
    supplier_ids: form.supplier_ids.includes(id)
      ? form.supplier_ids.filter((x) => x !== id)
      : [...form.supplier_ids, id],
  });

  // ── Segment-aware visible list + selection helpers ─────────────
  // The visible list, Select-all-matching, and Select-recommended
  // must all respect the current Supplier Type segment. Filtering is
  // done client-side so a single segment change updates every panel
  // without a server round-trip.
  const visibleSuppliers = useMemo(
    () => suppliers.filter((s) => matchesTypeSegment(s, form.type_segment)),
    [suppliers, form.type_segment],
  );
  const selectAllMatching = () => updateForm({
    supplier_ids: [...new Set([
      ...form.supplier_ids,
      ...visibleSuppliers.map((s) => s.id),
    ])],
  });
  const selectRecommended = () => {
    const recIds = visibleSuppliers
      .filter((s) => (s.email || '').trim()
        && ['valid_mx', 'verified'].includes(s.email_verification_status || '')
        && (s.status || '').toLowerCase() !== 'inactive'
        && (s.ranking ?? 0) >= 3)
      .map((s) => s.id);
    updateForm({ supplier_ids: [...new Set([...form.supplier_ids, ...recIds])] });
  };
  const clearSelection = () => updateForm({ supplier_ids: [] });

  // ── Filter-change confirmation ─────────────────────────────────
  // When a filter change would silently drop suppliers already in
  // the selection, prompt the operator before mutating state.
  const applyFilterChange = (field, value) => {
    const nextFilters = { ...supFilters };
    let nextSegment = form.type_segment;
    if (field === 'type_segment') nextSegment = value;
    else nextFilters[field] = value;

    // Compute which currently-selected suppliers would no longer
    // match the NEW segment (server-side filters like country / tag
    // trigger a refetch; the segment is the only client-side gate).
    const stillMatching = suppliers.filter(
      (s) => matchesTypeSegment(s, nextSegment),
    );
    const stillMatchingIds = new Set(stillMatching.map((s) => s.id));
    const wouldDrop = form.supplier_ids.filter((id) => {
      const supplier = suppliers.find((s) => s.id === id);
      if (!supplier) return false;   // not in the currently-loaded page
      return !stillMatchingIds.has(id);
    });

    if (wouldDrop.length > 0) {
      setPendingFilter({ field, value, wouldDrop });
      return;
    }
    // Nothing to drop — apply immediately.
    if (field === 'type_segment') updateForm({ type_segment: value });
    else setSupFilters(nextFilters);
  };
  const confirmPendingFilter = (drop) => {
    if (!pendingFilter) return;
    const { field, value, wouldDrop } = pendingFilter;
    if (drop && wouldDrop.length) {
      updateForm({
        supplier_ids: form.supplier_ids.filter((id) => !wouldDrop.includes(id)),
      });
    }
    if (field === 'type_segment') updateForm({ type_segment: value });
    else setSupFilters((f) => ({ ...f, [field]: value }));
    setPendingFilter(null);
  };

  // ── Templates ────────────────────────────────────────────────
  const applyTemplate = (key) => {
    if (!key) {
      // Explicit "no template" — clear both fields so subsequent picks
      // start fresh instead of appending to previous body.
      updateForm({ template_key: '', email_subject: '', email_body: '' });
      return;
    }
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    // Feb-2026 simplification — templates populate the VISIBLE subject
    // and body only. No hidden content is appended.
    updateForm({
      template_key: key,
      email_subject: tpl.subject || '',
      email_body: tpl.body || '',
    });
    toast.success(`Template applied: ${tpl.name}`);
  };

  // ── Validation (simplified — only 5 rules) ───────────────────
  const validation = useMemo(() => {
    const errs = [];
    if (!(form.email_subject || '').trim()) errs.push('Subject is required');
    if (!(form.email_body || '').trim()) errs.push('Email body is required');
    if (!form.supplier_ids.length) errs.push('At least one supplier must be selected');
    if (!(form.reply_to || '').trim()) errs.push('Reply-to is required');
    if (previewData && previewData.unresolved_placeholders?.length) {
      errs.push(`Unresolved placeholders: ${previewData.unresolved_placeholders.join(', ')}`);
    }
    return errs;
  }, [form, previewData]);

  // ── Actions ──────────────────────────────────────────────────
  const runPreview = async () => {
    try {
      const { data } = await previewRfq({
        email_subject: form.email_subject,
        email_body: form.email_body,
        product_request_text: form.product_request_text || productsToText(form.products),
        products: form.products,
        destination_country: form.destination_country,
        delivery_location: form.delivery_location,
        target_total_quantity: form.target_total_quantity === '' ? null : Number(form.target_total_quantity),
        target_budget: form.target_budget === '' ? null : Number(form.target_budget),
        shipping_term: form.shipping_term,
        response_due_date: form.response_due_date,
        warranty: form.warranty,
        condition: form.condition,
        additional_requirements: form.additional_requirements,
        supplier_id: form.supplier_ids[0],
      });
      setPreviewData(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Preview failed'); }
  };

  const runRecipientPreview = async () => {
    if (!campaignId) { toast.error('Save the draft first (footer → Save Draft)'); return; }
    try {
      const { data } = await rfqRecipientPreview(campaignId, {
        supplier_ids: form.supplier_ids,
        exclude_recent_contact_days: Number(form.exclude_recent_contact_days) || 0,
      });
      setRecipientPreview(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Recipient preview failed'); }
  };

  const runTestSend = async () => {
    if (!campaignId) { toast.error('Save the draft first'); return; }
    if (!testEmail) { toast.error('Enter a test email address'); return; }
    try {
      await rfqTestSend(campaignId, { to_email: testEmail, supplier_id: form.supplier_ids[0] });
      toast.success(`Test sent to ${testEmail}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Test send failed'); }
  };

  const buildPayload = () => ({
    ...form,
    campaign_name: form.campaign_name || suggestCampaignName(form),
    target_total_quantity: form.target_total_quantity === '' ? null : Number(form.target_total_quantity),
    target_budget: form.target_budget === '' ? null : Number(form.target_budget),
    exclude_recent_contact_days: Number(form.exclude_recent_contact_days) || 0,
    id: undefined, status: undefined, created_at: undefined, updated_at: undefined, sent_at: undefined,
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (!payload.campaign_name.trim()) { toast.error('Enter a subject or a campaign name first'); setSaving(false); return; }
      if (campaignId) {
        await updateRfqCampaign(campaignId, payload);
        toast.success('Draft updated');
      } else {
        const { data } = await createRfqCampaign(payload);
        setCampaignId(data.id);
        toast.success('Draft saved');
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const scheduleSend = async ({ confirm_do_not_rfq = false } = {}) => {
    if (!campaignId) { toast.error('Save the draft first'); return; }
    if (!form.scheduled_at) { toast.error('Pick a date/time'); return; }
    try {
      await rfqSchedule(campaignId, {
        scheduled_at: form.scheduled_at,
        confirm_do_not_rfq,
      });
      toast.success('Scheduled');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || 'Schedule failed';
      toast.error(message);
    }
  };

  const runSend = async ({ confirm_do_not_rfq = false } = {}) => {
    if (validation.length) { toast.error(`Fix ${validation.length} validation issue(s) first`); return; }
    if (!campaignId) { toast.error('Save the draft first'); return; }
    if (!confirmSend) { toast.error('Tick the confirmation box to proceed'); return; }
    setSaving(true);
    try {
      await updateRfqCampaign(campaignId, buildPayload());
      const { data } = await sendRfqCampaign(campaignId, {
        supplier_ids: form.supplier_ids,
        confirm_do_not_rfq,
      });
      if (data.sent > 0) {
        toast.success(`Sent ${data.sent}/${data.total} — Failed: ${data.failed}`);
        onSaved && onSaved();
        onClose();
      } else {
        // Feb-2026 audit fix — bulk send returned zero successes.
        // Surface the actual per-recipient reason(s) instead of a
        // silent "Failed: N" toast that hides Resend / provider
        // errors.
        const errs = data.errors || [];
        const summary = errs.length
          ? errs.slice(0, 3).map(
              (e) => `${e.count}× ${e.error}`,
            ).join(' · ')
          : 'No provider response';
        toast.error(
          `Send failed for all ${data.total} recipients. ${summary}`,
          { duration: 12000 },
        );
        onSaved && onSaved();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || 'Send failed';
      toast.error(message);
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="rfq-wizard-modal">
      <div className="w-full max-w-5xl bg-[#0A0A0A] border border-white/10 max-h-[94vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div>
            <h2 className="text-lg font-semibold">{editing ? 'Edit Supplier Email' : 'New Supplier Email'}</h2>
            {readOnly && <div className="text-xs text-amber-300 mt-0.5">Read-only — duplicate to edit</div>}
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="rfq-wizard-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5 overflow-x-auto" data-testid="rfq-wizard-steps">
          {STEP_LABELS.map((label, i) => (
            <button key={label} onClick={() => setStep(i)} data-testid={`rfq-wizard-step-${i}`}
                    className={`flex items-center gap-2 px-3 py-1.5 text-xs whitespace-nowrap ${step === i ? 'bg-[#00D4FF] text-black font-semibold' : 'bg-white/5 text-white/60 hover:text-white'}`}>
              <span className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: step === i ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)' }}>{i + 1}</span>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === 0 && (
            <Step1WriteEmail
              form={form}
              update={updateForm}
              onManualNameEdit={() => setManualNameEdit(true)}
              readOnly={readOnly}
              templates={templates}
              onApplyTemplate={applyTemplate}
              onInsertProductRequest={insertStructuredProductRequest}
              productHelpers={{ updateProduct, addProduct, duplicateProduct, deleteProduct, moveProduct }}
              generatedText={productsToText(form.products)}
            />
          )}
          {step === 1 && (
            <Step2Suppliers
              suppliers={suppliers}
              visibleSuppliers={visibleSuppliers}
              typeSegment={form.type_segment}
              filters={supFilters}
              onSegmentChange={(v) => applyFilterChange('type_segment', v)}
              onCountryChange={(v) => applyFilterChange('country', v)}
              onTagChange={(v) => applyFilterChange('tag', v)}
              setFilters={setSupFilters}
              selectedIds={form.supplier_ids}
              onToggle={toggleSupplier}
              onSelectAll={selectAllMatching}
              onSelectRecommended={selectRecommended}
              onClearSelection={clearSelection}
              excludeDays={form.exclude_recent_contact_days}
              setExcludeDays={(v) => updateForm({ exclude_recent_contact_days: v })}
              recipientPreview={recipientPreview}
              onRefreshRecipients={runRecipientPreview}
              campaignId={campaignId}
              readOnly={readOnly}
            />
          )}
          {step === 2 && (
            <Step3ReviewSend
              form={form}
              update={updateForm}
              validation={validation}
              onPreview={runPreview}
              previewData={previewData}
              onRefreshRecipients={runRecipientPreview}
              recipientPreview={recipientPreview}
              testEmail={testEmail}
              setTestEmail={setTestEmail}
              onTestSend={runTestSend}
              confirmSend={confirmSend}
              setConfirmSend={setConfirmSend}
              onSaveDraft={saveDraft}
              onSchedule={scheduleSend}
              onSend={runSend}
              saving={saving}
              readOnly={readOnly}
              selectedSuppliers={suppliers.filter((s) => form.supplier_ids.includes(s.id))}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/5 bg-[#0A0A0A]">
          <button onClick={saveDraft} disabled={saving || readOnly} data-testid="rfq-wizard-save-draft-footer"
                  className="flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:text-white disabled:opacity-40">
            <Save className="w-3.5 h-3.5" /> Save Draft
          </button>
          <div className="flex items-center gap-2">
            <button disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="rfq-wizard-back"
                    className="flex items-center gap-1 px-3 py-2 text-xs text-white/60 hover:text-white disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {step < 2 ? (
              <button onClick={() => setStep((s) => Math.min(2, s + 1))} data-testid="rfq-wizard-next"
                      className="flex items-center gap-1 px-4 py-2 text-xs bg-[#00D4FF] text-black font-semibold">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <div className="text-xs text-white/40">Use actions in Step 3</div>
            )}
          </div>
        </div>

        {/* Filter-change confirmation — surfaces whenever a segment/
            country/tag change would silently drop existing selections. */}
        {pendingFilter && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center" data-testid="rfq-wiz-filter-confirm">
            <div className="bg-[#0A0A0A] border border-[#00D4FF]/40 max-w-md w-full p-5 space-y-3">
              <div className="text-sm font-semibold">Drop non-matching selections?</div>
              <div className="text-xs text-white/70">
                Changing this filter would exclude <b>{pendingFilter.wouldDrop.length}</b>{' '}
                supplier(s) from your current selection. Remove them or keep them?
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => confirmPendingFilter(false)}
                  data-testid="rfq-wiz-filter-keep"
                  className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10"
                >Keep selections</button>
                <button
                  onClick={() => confirmPendingFilter(true)}
                  data-testid="rfq-wiz-filter-drop"
                  className="px-3 py-1.5 text-xs bg-[#00D4FF] text-black font-semibold"
                >Drop non-matching</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Reusable collapsible section ─────────────────────────────
const Collapsible = ({ title, testid, defaultOpen = false, children }) => {
  const [openS, setOpenS] = useState(defaultOpen);
  return (
    <div className="border border-white/10" data-testid={testid}>
      <button type="button" onClick={() => setOpenS(!openS)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs uppercase tracking-widest text-white/60 hover:text-white"
              data-testid={`${testid}-toggle`}>
        {title}
        <ChevronDown className={`w-4 h-4 transition-transform ${openS ? 'rotate-180' : ''}`} />
      </button>
      {openS && <div className="p-3 border-t border-white/5" data-testid={`${testid}-body`}>{children}</div>}
    </div>
  );
};

// ─── Step 1: Write Email ─────────────────────────────────────
const Step1WriteEmail = ({ form, update, onManualNameEdit, readOnly, templates, onApplyTemplate, onInsertProductRequest, productHelpers, generatedText }) => (
  <div className="space-y-4" data-testid="rfq-wizard-step1">
    <WField label="Subject *">
      <input disabled={readOnly} value={form.email_subject} onChange={(e) => update({ email_subject: e.target.value })}
             className="wipt" placeholder="e.g. Weekly inventory availability — Jan 2026" data-testid="rfq-wiz-subject" />
    </WField>
    <WField label="Email body *">
      <textarea disabled={readOnly} rows={16} value={form.email_body} onChange={(e) => update({ email_body: e.target.value })}
                className="wipt font-mono text-xs"
                placeholder="Write your message here. Free-form — no structure required.&#10;&#10;You may include personalization tokens: {CONTACT_NAME}, {COMPANY_NAME}."
                data-testid="rfq-wiz-body" />
    </WField>
    <div className="text-[10px] text-white/40">
      Personalization tokens: <code className="mx-1">{'{CONTACT_NAME}'}</code>, <code className="mx-1">{'{COMPANY_NAME}'}</code>.
      Anything else you type is sent verbatim.
    </div>

    <Collapsible title="Optional Details" testid="rfq-wiz-optional-details">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <WField label="Campaign name">
          <input disabled={readOnly} value={form.campaign_name}
                 onChange={(e) => { onManualNameEdit(); update({ campaign_name: e.target.value }); }}
                 className="wipt" placeholder="Auto-generated from subject + date"
                 data-testid="rfq-wiz-name" />
        </WField>
        <WField label="Template">
          <select disabled={readOnly} value={form.template_key || ''}
                  onChange={(e) => onApplyTemplate(e.target.value)}
                  className="wipt" data-testid="rfq-wiz-template">
            <option value="">— None (start blank) —</option>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
        </WField>
        <WField label="Destination country">
          <input disabled={readOnly} value={form.destination_country} onChange={(e) => update({ destination_country: e.target.value })} className="wipt" data-testid="rfq-wiz-country" />
        </WField>
        <WField label="Delivery location">
          <input disabled={readOnly} value={form.delivery_location} onChange={(e) => update({ delivery_location: e.target.value })} className="wipt" data-testid="rfq-wiz-delivery" />
        </WField>
        <WField label="Target quantity">
          <input disabled={readOnly} type="number" value={form.target_total_quantity} onChange={(e) => update({ target_total_quantity: e.target.value })} className="wipt" data-testid="rfq-wiz-qty" />
        </WField>
        <WField label="Target budget (USD)">
          <input disabled={readOnly} type="number" step="0.01" value={form.target_budget} onChange={(e) => update({ target_budget: e.target.value })} className="wipt" data-testid="rfq-wiz-budget" />
        </WField>
        <WField label="Response due date">
          <input disabled={readOnly} type="date" value={form.response_due_date} onChange={(e) => update({ response_due_date: e.target.value })} className="wipt" data-testid="rfq-wiz-due" />
        </WField>
        <WField label="Shipping term">
          <select disabled={readOnly} value={form.shipping_term} onChange={(e) => update({ shipping_term: e.target.value })} className="wipt" data-testid="rfq-wiz-shipping">
            <option value="">— select —</option>
            {SHIPPING_TERMS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </WField>
        <WField label="Condition">
          <select disabled={readOnly} value={form.condition} onChange={(e) => update({ condition: e.target.value })} className="wipt" data-testid="rfq-wiz-condition">
            <option value="">— select —</option>
            {CONDITIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </WField>
        <WField label="Warranty">
          <select disabled={readOnly} value={form.warranty} onChange={(e) => update({ warranty: e.target.value })} className="wipt" data-testid="rfq-wiz-warranty">
            <option value="">— select —</option>
            {WARRANTIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </WField>
        <div className="lg:col-span-2">
          <WField label="Additional requirements">
            <textarea disabled={readOnly} rows={2} value={form.additional_requirements} onChange={(e) => update({ additional_requirements: e.target.value })} className="wipt" data-testid="rfq-wiz-additional" />
          </WField>
        </div>

        {/* Insert Structured Product Request tool */}
        <div className="lg:col-span-2 border border-white/10 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-white/60">
              Structured product builder (optional — never required)
            </div>
            <div className="flex items-center gap-2">
              <button disabled={readOnly} onClick={productHelpers.addProduct}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] bg-white/5 hover:bg-white/10 disabled:opacity-40"
                      data-testid="rfq-wiz-add-product">
                <Plus className="w-3 h-3" /> Add Row
              </button>
              <button disabled={readOnly || !form.products.length} onClick={onInsertProductRequest}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] bg-[#00D4FF]/15 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/25 disabled:opacity-40"
                      data-testid="rfq-wiz-insert-product-request">
                Insert Structured Product Request
              </button>
            </div>
          </div>
          {form.products.length === 0 && (
            <div className="text-[11px] text-white/40">No product rows. Click <b>Add Row</b> to build a structured request, or leave empty for a free-form email.</div>
          )}
          {form.products.map((p, i) => (
            <div key={i} className="grid grid-cols-2 lg:grid-cols-5 gap-1" data-testid={`rfq-wiz-product-row-${i}`}>
              <input disabled={readOnly} value={p.brand} onChange={(e) => productHelpers.updateProduct(i, { brand: e.target.value })} placeholder="Brand" className="wipt wipt-sm" data-testid={`rfq-wiz-brand-${i}`} />
              <input disabled={readOnly} value={p.model} onChange={(e) => productHelpers.updateProduct(i, { model: e.target.value })} placeholder="Model" className="wipt wipt-sm" data-testid={`rfq-wiz-model-${i}`} />
              <input disabled={readOnly} value={p.ram} onChange={(e) => productHelpers.updateProduct(i, { ram: e.target.value })} placeholder="RAM" className="wipt wipt-sm" />
              <input disabled={readOnly} type="number" value={p.quantity} onChange={(e) => productHelpers.updateProduct(i, { quantity: e.target.value })} placeholder="Qty" className="wipt wipt-sm" data-testid={`rfq-wiz-qty-${i}`} />
              <div className="flex items-center gap-1 justify-end">
                <button disabled={readOnly || i === 0} onClick={() => productHelpers.moveProduct(i, -1)} className="p-1 text-white/40 hover:text-white disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                <button disabled={readOnly || i === form.products.length - 1} onClick={() => productHelpers.moveProduct(i, 1)} className="p-1 text-white/40 hover:text-white disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                <button disabled={readOnly} onClick={() => productHelpers.duplicateProduct(i)} className="p-1 text-white/40 hover:text-white disabled:opacity-30"><Copy className="w-3 h-3" /></button>
                <button disabled={readOnly} onClick={() => productHelpers.deleteProduct(i)} className="p-1 text-white/40 hover:text-red-400 disabled:opacity-30"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
          {generatedText && (
            <pre className="bg-black/40 border border-white/10 px-3 py-2 text-[11px] text-white/70 whitespace-pre-wrap mt-2">
{generatedText}
            </pre>
          )}
        </div>
      </div>
    </Collapsible>
  </div>
);

// ─── Step 2: Suppliers ────────────────────────────────────────
const Step2Suppliers = ({
  suppliers, visibleSuppliers, typeSegment, filters,
  onSegmentChange, onCountryChange, onTagChange,
  setFilters, selectedIds, onToggle, onSelectAll,
  onSelectRecommended, onClearSelection, excludeDays, setExcludeDays,
  recipientPreview, onRefreshRecipients, campaignId, readOnly,
}) => (
  <div className="space-y-3" data-testid="rfq-wizard-step2">
    {/* Primary filters (always visible) */}
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
      <input disabled={readOnly} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
             placeholder="Search company / contact / email" className="wipt wipt-sm" data-testid="rfq-wiz-sup-q" />
      <input disabled={readOnly} value={filters.country}
             onChange={(e) => onCountryChange(e.target.value)}
             placeholder="Country" className="wipt wipt-sm" data-testid="rfq-wiz-sup-country" />
      <input disabled={readOnly} value={filters.tag}
             onChange={(e) => onTagChange(e.target.value)}
             placeholder="Inventory category / tag" className="wipt wipt-sm" data-testid="rfq-wiz-sup-tag" />
      <select disabled={readOnly} value={typeSegment || ''}
              onChange={(e) => onSegmentChange(e.target.value)}
              className="wipt wipt-sm" data-testid="rfq-wiz-sup-type-segment">
        {SUPPLIER_TYPE_SEGMENTS.map((seg) => (
          <option key={seg.value || 'all'} value={seg.value}>{seg.label}</option>
        ))}
      </select>
    </div>

    {/* More Filters (collapsed by default) */}
    <Collapsible title="More Filters" testid="rfq-wiz-more-filters">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <select disabled={readOnly} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="wipt wipt-sm">
          <option value="">All statuses</option>
          {['new', 'contacted', 'quoted', 'approved', 'rejected', 'inactive'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select disabled={readOnly} value={filters.email_verification_status} onChange={(e) => setFilters({ ...filters, email_verification_status: e.target.value })} className="wipt wipt-sm">
          <option value="">All email statuses</option>
          {['unverified', 'valid_mx', 'risky', 'invalid', 'unknown', 'verified'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select disabled={readOnly} value={filters.enrichment_status} onChange={(e) => setFilters({ ...filters, enrichment_status: e.target.value })} className="wipt wipt-sm">
          <option value="">All enrichment</option>
          {['needs_enrichment', 'enriched', 'verified', 'invalid', 'no_contact_found'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select disabled={readOnly} value={excludeDays} onChange={(e) => setExcludeDays(Number(e.target.value))} className="wipt wipt-sm" data-testid="rfq-wiz-recent-days">
          {RECENT_CONTACT_OPTIONS.map((o) => <option key={o.value} value={o.value}>Exclude recent: {o.label}</option>)}
        </select>
      </div>
    </Collapsible>

    <div className="flex items-center gap-2">
      <button disabled={readOnly} onClick={onSelectAll} className="px-2 py-1 text-xs bg-white/5 hover:bg-white/10 disabled:opacity-40" data-testid="rfq-wiz-select-all">Select all matching</button>
      <button disabled={readOnly} onClick={onSelectRecommended} className="px-2 py-1 text-xs bg-white/5 hover:bg-white/10 disabled:opacity-40" data-testid="rfq-wiz-select-recommended">Select recommended</button>
      <button disabled={readOnly} onClick={onClearSelection} className="px-2 py-1 text-xs bg-white/5 hover:bg-white/10 disabled:opacity-40" data-testid="rfq-wiz-clear-selection">Clear</button>
      <div className="ml-auto text-xs text-white/60" data-testid="rfq-wiz-selected-count">{selectedIds.length} selected · {visibleSuppliers.length} matching</div>
      <button disabled={readOnly || !campaignId} onClick={onRefreshRecipients} title={campaignId ? '' : 'Save the draft first'}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/10 disabled:opacity-30" data-testid="rfq-wiz-recipient-preview-btn">
        <Users className="w-3.5 h-3.5" /> Recipient Preview
      </button>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="border border-white/10 max-h-[50vh] overflow-y-auto" data-testid="rfq-wiz-supplier-list">
        {!visibleSuppliers.length && <div className="p-4 text-xs text-white/40">No suppliers match. Adjust filters.</div>}
        {visibleSuppliers.map((s) => (
          <label key={s.id} className="flex items-center gap-3 px-3 py-2 border-b border-white/5 hover:bg-white/5 cursor-pointer">
            <input disabled={readOnly} type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => onToggle(s.id)} data-testid={`rfq-wiz-sup-${s.id}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">
                {s.company_name}
                {s.supplier_type ? (
                  <span
                    data-testid={`rfq-wiz-sup-type-${s.id}`}
                    className={`ml-2 text-[10px] px-1.5 py-0.5 border ${
                      RESTRICTED_TYPES.has(s.supplier_type)
                        ? 'border-amber-400/40 text-amber-300'
                        : 'border-white/20 text-white/60'
                    }`}
                  >
                    {s.supplier_type}
                  </span>
                ) : (
                  <span
                    data-testid={`rfq-wiz-sup-type-${s.id}`}
                    className="ml-2 text-[10px] px-1.5 py-0.5 border border-white/10 text-white/40"
                  >
                    Unclassified
                  </span>
                )}
              </div>
              <div className="text-[11px] text-white/50 truncate">
                {s.email || '(no email)'} · {s.country || '—'} · {(s.email_verification_status || '—')} · ranking {s.ranking ?? '—'}
              </div>
              <div className="text-[10px] text-white/30 truncate">
                Last contacted: {s.last_contacted_at ? new Date(s.last_contacted_at).toLocaleDateString() : '—'} · Last RFQ: {s.last_rfq_date ? new Date(s.last_rfq_date).toLocaleDateString() : '—'}
              </div>
            </div>
          </label>
        ))}
      </div>

      <div className="border border-white/10 p-3 max-h-[50vh] overflow-y-auto" data-testid="rfq-wiz-recipient-preview-panel">
        <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Recipient Preview</div>
        {!recipientPreview && (
          <div className="text-xs text-white/40">
            Save the draft and click <b>Recipient Preview</b> to see who will actually receive the email.
            Invalid, bounced, opted-out, duplicate, and inactive supplier emails are auto-excluded.
          </div>
        )}
        {recipientPreview && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <Bucket label="Raw selection" value={recipientPreview.counts.raw} />
              <Bucket label="Final recipients" value={recipientPreview.counts.final} highlight />
              <Bucket label="Duplicates" value={recipientPreview.counts.duplicates} />
              <Bucket label="Invalid emails" value={recipientPreview.counts.invalid} />
              <Bucket label="Bounced" value={recipientPreview.counts.bounced} />
              <Bucket label="Opted-out" value={recipientPreview.counts.opted_out} />
              <Bucket label="Recently contacted" value={recipientPreview.counts.recently_contacted} />
              <Bucket label="Inactive" value={recipientPreview.counts.inactive} />
            </div>
            {recipientPreview.excluded?.length > 0 && (
              <div>
                <div className="text-xs uppercase text-white/40 mb-1">Excluded ({recipientPreview.excluded.length})</div>
                <div className="max-h-40 overflow-y-auto border border-white/10">
                  {recipientPreview.excluded.map((r, i) => (
                    <div key={i} className="px-2 py-1.5 border-b border-white/5 text-[11px]">
                      <div className="text-white/70">{r.company_name} — {r.email || '(no email)'}</div>
                      <div className="text-red-300/80">{r.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

const Bucket = ({ label, value, highlight }) => (
  <div className={`px-2 py-2 border ${highlight ? 'border-[#00D4FF]/40 bg-[#00D4FF]/5' : 'border-white/10'}`}>
    <div className="text-white/50 text-[10px] uppercase tracking-widest">{label}</div>
    <div className={`text-lg font-bold ${highlight ? 'text-[#00D4FF]' : 'text-white'}`}>{value}</div>
  </div>
);

// ─── Step 3: Review & Send ────────────────────────────────────
const Step3ReviewSend = ({
  form, update, validation, onPreview, previewData,
  onRefreshRecipients, recipientPreview, testEmail, setTestEmail, onTestSend,
  confirmSend, setConfirmSend, onSaveDraft, onSchedule, onSend, saving, readOnly,
  selectedSuppliers = [],
}) => {
  const eligibleCount = recipientPreview?.counts?.final ?? form.supplier_ids.length;
  const segmentLabel = getSegmentLabel(form.type_segment);
  const typeCounts = countByType(selectedSuppliers);
  const restrictedRecipients = flagRestrictedSuppliers(selectedSuppliers);
  const doNotRfqRecipients = selectedSuppliers.filter(
    (s) => (s.supplier_type || '').trim() === DO_NOT_RFQ_TYPE,
  );
  const [confirmDoNotRfq, setConfirmDoNotRfq] = useState(false);
  const sendBlocked = doNotRfqRecipients.length > 0 && !confirmDoNotRfq;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rfq-wizard-step3">
      <div className="space-y-3">
        <div className="border border-white/10 p-3 space-y-2" data-testid="rfq-wiz-segment-summary">
          <div className="text-xs uppercase tracking-widest text-white/60">Campaign segment</div>
          <div className="text-sm" data-testid="rfq-wiz-segment-label">{segmentLabel}</div>
          <div className="grid grid-cols-2 gap-1 text-[11px] text-white/70" data-testid="rfq-wiz-type-counts">
            {Object.entries(typeCounts)
              .filter(([, v]) => v > 0)
              .map(([t, v]) => (
                <div key={t} className="flex justify-between border-b border-white/5 py-0.5">
                  <span className={RESTRICTED_TYPES.has(t) ? 'text-amber-300' : ''}>{t}</span>
                  <b className="text-white">{v}</b>
                </div>
              ))}
            {Object.values(typeCounts).every((v) => v === 0) && (
              <div className="text-white/40">No suppliers selected.</div>
            )}
          </div>
          {restrictedRecipients.length > 0 && (
            <div
              className="text-[11px] text-amber-300 border border-amber-400/30 bg-amber-400/5 p-2"
              data-testid="rfq-wiz-restricted-warning"
            >
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              {restrictedRecipients.length} recipient(s) marked
              <b> Review Before RFQ</b> or <b>Do Not RFQ</b>. Confirm each is
              intentional before sending.
            </div>
          )}
          {doNotRfqRecipients.length > 0 && (
            <label
              className="flex items-start gap-2 text-[11px] cursor-pointer border border-red-400/40 bg-red-400/5 p-2"
              data-testid="rfq-wiz-do-not-rfq-confirm-wrap"
            >
              <input
                type="checkbox"
                checked={confirmDoNotRfq}
                onChange={(e) => setConfirmDoNotRfq(e.target.checked)}
                data-testid="rfq-wiz-do-not-rfq-confirm"
              />
              <span className="text-red-200">
                I explicitly confirm sending to <b>{doNotRfqRecipients.length}</b>{' '}
                supplier(s) marked <b>{DO_NOT_RFQ_TYPE}</b>.
              </span>
            </label>
          )}
        </div>

        <div className="border border-white/10 p-3 space-y-2" data-testid="rfq-wiz-visible-preview">
          <div className="text-xs uppercase tracking-widest text-white/60">What suppliers will see</div>
          <div className="text-xs">
            <span className="text-white/40">Subject:</span> <b>{form.email_subject || '(empty)'}</b>
          </div>
          <pre className="bg-white text-black text-xs px-3 py-2 whitespace-pre-wrap max-h-[40vh] overflow-y-auto">{form.email_body || '(empty)'}</pre>
        </div>

        <Collapsible title="Sender Options" testid="rfq-wiz-sender-options">
          <div className="grid grid-cols-2 gap-3">
            <WField label="Sender profile ID (optional)">
              <input disabled={readOnly} value={form.sender_profile_id} onChange={(e) => update({ sender_profile_id: e.target.value })} className="wipt" placeholder="default profile if empty" data-testid="rfq-wiz-sender" />
            </WField>
            <WField label="Reply-to *">
              <input disabled={readOnly} value={form.reply_to} onChange={(e) => update({ reply_to: e.target.value })} className="wipt" placeholder="sales@luxortek.com" data-testid="rfq-wiz-reply-to" />
            </WField>
            <WField label="Preheader">
              <input disabled={readOnly} value={form.preheader} onChange={(e) => update({ preheader: e.target.value })} className="wipt" placeholder="Optional preview line" data-testid="rfq-wiz-preheader" />
            </WField>
          </div>
        </Collapsible>
      </div>

      <div className="space-y-3">
        <div className="border border-white/10 p-3 space-y-2">
          <div className="text-xs uppercase tracking-widest text-white/60">Validation</div>
          {validation.length === 0 ? (
            <div className="flex items-center gap-2 text-emerald-300 text-xs" data-testid="rfq-wiz-validation-ok">
              <CheckCircle2 className="w-4 h-4" /> Ready to send
            </div>
          ) : (
            <ul className="text-xs text-red-300 space-y-1" data-testid="rfq-wiz-validation-errors">
              {validation.map((v, i) => (
                <li key={i} className="flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {v}</li>
              ))}
            </ul>
          )}
          <div className="text-xs text-white/50">Eligible recipients: <b className="text-white">{eligibleCount}</b></div>
        </div>

        <div className="border border-white/10 p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onPreview} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10" data-testid="rfq-wiz-preview-btn"><Eye className="w-3.5 h-3.5" /> Preview</button>
            <button onClick={onRefreshRecipients} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10" data-testid="rfq-wiz-recipients-btn"><Users className="w-3.5 h-3.5" /> Preview Recipients</button>
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" className="wipt wipt-sm" style={{ maxWidth: 200 }} data-testid="rfq-wiz-test-email" />
            <button onClick={onTestSend} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10" data-testid="rfq-wiz-test-send-btn"><TestTube2 className="w-3.5 h-3.5" /> Send Test</button>
          </div>
        </div>

        <div className="border border-white/10 p-3">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Schedule</div>
          <div className="flex items-center gap-2">
            <input type="datetime-local" value={form.scheduled_at || ''} onChange={(e) => update({ scheduled_at: e.target.value })} className="wipt wipt-sm" data-testid="rfq-wiz-schedule-at" />
            <button
              onClick={() => onSchedule({ confirm_do_not_rfq: confirmDoNotRfq })}
              disabled={readOnly || (doNotRfqRecipients.length > 0 && !confirmDoNotRfq)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 disabled:opacity-40"
              data-testid="rfq-wiz-schedule-btn"
            ><Calendar className="w-3.5 h-3.5" /> Schedule</button>
          </div>
        </div>

        <div className="border border-red-400/30 bg-red-400/5 p-3 space-y-2">
          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={confirmSend} onChange={(e) => setConfirmSend(e.target.checked)} data-testid="rfq-wiz-confirm-send" />
            <span>I have reviewed the recipients and email body. Send this email to <b>{eligibleCount}</b> supplier(s).</span>
          </label>
          <div className="flex items-center gap-2">
            <button onClick={onSaveDraft} disabled={saving || readOnly} className="flex items-center gap-1 px-3 py-2 text-xs bg-white/5 hover:bg-white/10 disabled:opacity-40" data-testid="rfq-wiz-save-draft"><Save className="w-3.5 h-3.5" /> Save Draft</button>
            <button
              onClick={() => onSend({ confirm_do_not_rfq: confirmDoNotRfq })}
              disabled={saving || readOnly || !confirmSend || validation.length > 0 || sendBlocked}
              className="flex items-center gap-1 px-4 py-2 text-xs bg-[#00D4FF] text-black font-semibold disabled:opacity-40"
              data-testid="rfq-wiz-send-btn"
            >
              <Send className="w-3.5 h-3.5" /> {saving ? 'Sending…' : 'Send'}
            </button>
          </div>
          {sendBlocked && (
            <div className="text-[11px] text-red-300" data-testid="rfq-wiz-do-not-rfq-block">
              Send is blocked until you explicitly confirm the Do Not RFQ recipients above.
            </div>
          )}
        </div>

        {previewData && (
          <div className="border border-white/10 p-3 space-y-2" data-testid="rfq-wiz-preview-panel">
            <div className="text-xs uppercase tracking-widest text-white/60">Personalized Preview (first supplier)</div>
            <div className="text-xs">
              <span className="text-white/40">Subject:</span> {previewData.subject || '(empty)'}
            </div>
            <pre className="bg-white text-black text-xs px-3 py-2 whitespace-pre-wrap max-h-[40vh] overflow-y-auto">{previewData.rendered_body}</pre>
            {previewData.unresolved_placeholders?.length > 0 && (
              <div className="text-xs text-red-300">Unresolved: {previewData.unresolved_placeholders.join(', ')}</div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .wipt{width:100%;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);padding:0.5rem 0.7rem;font-size:0.8rem;color:white;outline:none}
        .wipt:focus{border-color:rgba(0,212,255,0.5)}
        .wipt-sm{padding:0.35rem 0.55rem;font-size:0.75rem}
        .wipt:disabled{opacity:0.5;cursor:not-allowed}
      `}</style>
    </div>
  );
};

const WField = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-[10px] uppercase tracking-widest text-white/50 mb-1 block">{label}</span>
    {children}
  </label>
);
